import { useMemo } from "react";
import { StyleSheet, Text, View } from "react-native";
import { useTranslation } from "react-i18next";

import { cadenceMs, freshness, type Point, type Sex } from "@heraion/shared";

import { ChartHead } from "../../charts/ChartHead";
import { useChartReadout } from "../../charts/pointSelection";
import { WagmiLineChart, type ChartSeries } from "../../charts/WagmiLineChart";
import { StaleBadge } from "../../primitives/StaleBadge";
import type { UiTheme } from "../../theme";

// One chart of MeasurementTrends, split out so each chart owns its own
// selection (PG-64 fetta 3): the header quotes the tapped point, «value ·
// date», the latest by default. #388: the chart's legend names the curve by
// the series' `name`. #290: one curve, the Health Connect one is gone.

export type MeasurementSeries = {
  key: string;
  title: string;
  points: Point[];
  yFormat: (n: number) => string;
};

type Props = {
  series: MeasurementSeries;
  sex: Sex | undefined;
  theme: UiTheme;
  nowMs: number;
};

export function MeasurementTrendChart({ series: s, sex, theme, nowMs }: Props) {
  const { t } = useTranslation();
  const styles = useMemo(() => makeStyles(theme), [theme]);
  const { selection, readout, readoutProps } = useChartReadout(s.points, s.yFormat, true);
  const chartSeries: ChartSeries[] = [
    {
      key: s.key,
      points: s.points,
      variant: "primary",
      headline: true,
      // PG-53: the legend's key names the curve with it.
      name: t("mobile.body.estimateSeriesName", { defaultValue: "Estimate" }),
    },
  ];
  // The JL estimate is what the header reports, so it is the series whose
  // silence matters and the one a tap reads (`headline`).
  const xs = s.points.map((p) => p.x);
  const fresh = freshness(xs, nowMs);
  const lastAt = xs[xs.length - 1];
  return (
    <View style={styles.chartBlock}>
      <ChartHead
        theme={theme}
        title={s.title}
        readout={readout}
        readoutProps={readoutProps}
        prominent
      />
      {fresh.known && fresh.stale && lastAt !== undefined ? (
        <View style={styles.ageRow}>
          <StaleBadge theme={theme} atMs={lastAt} cadenceMs={cadenceMs(xs)} />
        </View>
      ) : null}
      <WagmiLineChart
        series={chartSeries}
        yFormat={s.yFormat}
        theme={theme}
        nowMs={nowMs}
        selection={selection}
      />
      {/* MS-2: body fat is a tape measurement, not a scale reading, it moves
          with waist/neck (and hips for women), not weight. A plain factual
          line, gender-aware from the sex already in scope. */}
      {s.key === "fat" ? (
        <Text style={styles.provenance}>
          {sex === "female"
            ? t("mobile.body.bodyFatProvenanceWithHips", {
                defaultValue: "Body fat is calculated from waist, neck and hips.",
              })
            : t("mobile.body.bodyFatProvenance", {
                defaultValue: "Body fat is calculated from waist and neck.",
              })}
        </Text>
      ) : null}
    </View>
  );
}

function makeStyles(theme: UiTheme) {
  const { palette: p, spacing: s, typography: ty } = theme;
  return StyleSheet.create({
    chartBlock: { gap: s.s1, marginTop: s.s3 },
    ageRow: { flexDirection: "row" },
    // MS-2 provenance line: explanatory content, sm.
    provenance: { color: p.ink3, fontSize: ty.sizes.sm, marginTop: s.s1 },
  });
}
